import React, { useState } from "react";

import { SelectChangeEvent, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import type { SettingsModuleProps } from "@app/analytics/account/types";
import {
  SettingSectionTitle,
  SwitchControlLabel,
} from "@app/analytics/account/WorkspaceSettings/components/styles";
import WorkspaceCanEditDropdown from "@app/analytics/account/WorkspaceSettings/components/WorkspaceCanEditDropdown";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { TOOLTIPS } from "@app/common/content";
import type { EnterpriseWorkspaceSettings } from "@app/common/workspaces/types";

import Alert from "@rui/Alert";
import ConfirmationModal from "@rui/ConfirmationModal";
import Stack from "@rui/Stack";
import { Bold } from "@rui/styles";
import Switch from "@rui/Switch";

const List = styled("ul")({
  marginBlockStart: "4px",
  marginBlockEnd: 0,
  paddingInlineStart: "24px",
});

interface DistributionSettingsProps extends SettingsModuleProps {
  onDropdownChange: (
    settingValue: boolean,
    settingName: keyof EnterpriseWorkspaceSettings
  ) => void;
  onPreferenceChange: (
    preferenceName: keyof EnterpriseWorkspaceSettings,
    type: "toggle" | "value"
  ) => (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent) => void;
}

const DistributionSettings = ({
  settings,
  onDropdownChange,
  onPreferenceChange,
  onSettingUpdate,
  workspaceIsInactive,
}: DistributionSettingsProps) => {
  const [
    showDisableMeetingRecapConfirmation,
    setShowDisableMeetingRecapConfirmation,
  ] = useState(false);
  const [showDisablePreReadsConfirmation, setShowDisablePreReadsConfirmation] =
    useState(false);

  const handleToggleSetting = (
    setting: keyof EnterpriseWorkspaceSettings,
    checked: boolean
  ) => {
    onSettingUpdate({ [setting]: checked });
  };

  const handleToggleMeetingRecaps = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      handleToggleSetting("sendMeetingRecap", checked);
    } else {
      setShowDisableMeetingRecapConfirmation(true);
    }
  };

  const handleTogglePreReads = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    if (checked) {
      handleToggleSetting("sendPreRead", checked);
    } else {
      setShowDisablePreReadsConfirmation(true);
    }
  };

  const handleToggleTopicSummaries = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    handleToggleSetting("sendTopicSummary", checked);
  };

  const handleConfirmDisableRecaps = () => {
    handleToggleSetting("sendMeetingRecap", false);
    setShowDisableMeetingRecapConfirmation(false);
  };

  const handleConfirmDisablePreReads = () => {
    handleToggleSetting("sendPreRead", false);
    setShowDisablePreReadsConfirmation(false);
  };

  return (
    <>
      <SettingSectionTitle>Distribution</SettingSectionTitle>
      <Stack spacing={4}>
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <SwitchControlLabel
            disabled={workspaceIsInactive}
            sx={{ alignItems: "flex-start" }}
            control={
              <Switch
                checked={settings.sendMeetingRecap}
                onChange={handleToggleMeetingRecaps}
              />
            }
            label={
              <SettingsTitle
                title="Meeting Recaps"
                subtitle="Workspace members who are the report owner can distribute recaps to meeting participants (via email, Slack, etc.)"
              />
            }
          />
        </UpsellTooltipWrapper>
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <SwitchControlLabel
            disabled={workspaceIsInactive}
            sx={{ alignItems: "flex-start" }}
            control={
              <Switch
                checked={Boolean(settings.sendPreRead)}
                onChange={handleTogglePreReads}
              />
            }
            label={
              <SettingsTitle
                title="Meeting Pre-Reads"
                subtitle="Workspace members who are the report owner can distribute a summary of the previous meeting before an upcoming recurring event (via email, Slack, etc.)"
              />
            }
          />
        </UpsellTooltipWrapper>
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <SwitchControlLabel
            disabled={workspaceIsInactive}
            sx={{ alignItems: "flex-start" }}
            control={
              <Switch
                checked={Boolean(settings.sendTopicSummary)}
                onChange={handleToggleTopicSummaries}
              />
            }
            label={
              <SettingsTitle
                title="Topic summaries"
                subtitle="Workspace members can automatically receive new email and messaging Readouts and updates (via email, Slack, Teams)"
              />
            }
          />
        </UpsellTooltipWrapper>
        <Stack spacing={2}>
          <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
            <SwitchControlLabel
              disabled={workspaceIsInactive}
              sx={{ alignItems: "flex-start" }}
              control={
                <Switch
                  checked={settings.shareLinkInBotMessage}
                  onChange={onPreferenceChange(
                    "shareLinkInBotMessage",
                    "toggle"
                  )}
                />
              }
              label={
                <SettingsTitle
                  title="Send chat message"
                  subtitle="During the meeting, automatically share a link to reporting in the chat"
                />
              }
            />
          </UpsellTooltipWrapper>
          <WorkspaceCanEditDropdown
            disabled={workspaceIsInactive}
            onUpdate={onDropdownChange}
            name="shareLinkInBotMessageIsUserEditable"
            selectedValue={settings.shareLinkInBotMessageIsUserEditable}
            tooltip={
              workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
            }
          />
        </Stack>
      </Stack>

      <ConfirmationModal
        title="Disable all meeting recaps?"
        open={showDisableMeetingRecapConfirmation}
        onClose={() => setShowDisableMeetingRecapConfirmation(false)}
        onConfirm={handleConfirmDisableRecaps}
        size="md"
        confirmProps={{
          label: "Yes, disable",
          type: "negative",
        }}
      >
        <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
          Disabling meeting recaps prevents Workspace members from:
          <List>
            <li>Receiving their own meeting recaps</li>
            <li>Distributing recaps to meeting participants</li>
          </List>
        </Typography>
        <Alert severity="info" embedded alignItems="flex-start">
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            If you would rather prevent external participants from accessing
            meeting reports, set Report Sharing {"> "}
            <Bold>External Participant Access</Bold> to "None"
          </Typography>
        </Alert>
      </ConfirmationModal>
      <ConfirmationModal
        title="Disable all meeting Pre-Reads?"
        open={showDisablePreReadsConfirmation}
        onClose={() => setShowDisablePreReadsConfirmation(false)}
        onConfirm={handleConfirmDisablePreReads}
        size="md"
        confirmProps={{
          label: "Yes, disable",
          type: "negative",
        }}
      >
        <Typography variant="bodyPrimaryReg" color="shades.neutral.700">
          Disabling meeting Pre-Reads prevents Workspace members from:
          <List>
            <li>Receiving their own meeting Pre-Reads</li>
            <li>Distributing Pre-Reads to meeting participants</li>
          </List>
        </Typography>
        <Alert severity="info" embedded alignItems="flex-start">
          <Typography variant="bodySecondaryReg" color="shades.neutral.600">
            Pre-Reads are only distributed to people with prior report access
            who are also invited to the upcoming recurring meeting.
          </Typography>
        </Alert>
      </ConfirmationModal>
    </>
  );
};

export default DistributionSettings;
